import React from "react"
import { classnames, TTailwindString } from "tailwindcss-classnames"
import ButtonSpinner from "./ButtonSpinner";

const BASE_STYLE = classnames(
  "inline-flex",
  "appearance-none",
  "items-center",
  "justify-center",
  "transition",
  "duration-150",
  "select-none",
  "relative",
  "whitespace-no-wrap",
  "align-middle",
  "outline-none",
  "rounded-md",
  "leading-tight",
  "font-semibold",
);

export const disabledStyle = classnames("opacity-50", "cursor-not-allowed");

const SIZE = {
  xl: ["h-16", "min-w-16", "text-2xl", "px-8"],
  lg: ["h-12", "min-w-12", "text-lg", "px-6"],
  md: ["h-10", "min-w-10", "text-md", "px-4"],
  sm: ["h-8", "min-w-8", "text-sm", "px-3"],
  xs: ["h-6", "min-w-6", "text-xs", "px2"],
}

const SOLID_VARIANT = {
  gray: ["bg-gray-100", "hover:bg-gray-200 active:bg-gray-300"],
  red: ["bg-red-500", "text-white", "hover:bg-red-600", "active:bg-red-600"],
}

const GHOST_VARIANT = {
  gray: ["hover:bg-gray-100", "active:bg-gray-200"],
  red: ["text-red-500", "bg-transparent", "hover:bg-red-50", "active:bg-red-100"],
}

const OUTLINE_VARIANT = {
  gray: ["border-gray-200"],
  red: ["border-3", "border-red-500", ...GHOST_VARIANT.red],
}

const VARIANTS = {
  solid: SOLID_VARIANT,
  ghost: GHOST_VARIANT,
  outline: OUTLINE_VARIANT,
}

export type ButtonVariant = "solid" | "outline" | "ghost"
export type ButtonVariantColor = "gray" | "red"

export type NativeButtonProps = {
  type?: "button" | "submit"
  onClick?: React.MouseEventHandler<HTMLElement>
} & Omit<React.ButtonHTMLAttributes<any>, "onClick">

export interface ButtonProps extends NativeButtonProps {
  size?: "xs" | "sm" | "md" | "lg" | "xl"
  variant?: ButtonVariant
  variantColor?: ButtonVariantColor
  block?: boolean
  children?: React.ReactNode | string
  className?: string | TTailwindString
  disabled?: boolean
  isLoading?: boolean
  loadingText?: string
  fullWidth?: boolean
  leftIcon?: React.ReactElement
  rightIcon?: React.ReactElement
}

export const Button: React.FC<ButtonProps> = ({
  size = "md",
  variantColor = "gray",
  variant = "solid",
  className,
  children,
  leftIcon,
  rightIcon,
  block = false,
  disabled = false,
  type = "button",
  isLoading,
  loadingText,
  fullWidth,
  ...props
}) => {
  const defaultStyle = classnames(BASE_STYLE, {
    "opacity-50": disabled || isLoading,
    "cursor-not-allowed": disabled || isLoading,
    "w-full": fullWidth,
  })

  const classes = [
    defaultStyle,
    SIZE[size].join(" "),
    VARIANTS[variant][variantColor].join(" "),
    className,
  ]
    .filter(Boolean)
    .join(" ")
  
  const hasLoadingLabel = !!loadingText
  const loadingWrapperClasses = classnames("leading-normal", "inline-flex", "align-middle", {
    "mr-2": hasLoadingLabel,
    "relative": hasLoadingLabel,
    "absolute": !hasLoadingLabel,
  })
  return (
    <button disabled={disabled || isLoading} className={classes} type={type} {...props}>
      {leftIcon && !isLoading && (
        <span className="mr-2">
          {leftIcon}
        </span>
      )}
      {isLoading && (
        <div className={loadingWrapperClasses}>
          <ButtonSpinner size="sm" />
        </div>
      )}
      {isLoading
       ? loadingText || <span className="opacity-0">{children}</span>
       : children}

      {rightIcon && !isLoading && (
        <span className="mr-2">
          {rightIcon}
        </span>
      )}
    </button>
  )
}

export default Button
